/**
 * Helper for invoking SpacetimeDB procedures and unwrapping the JSON envelope
 * every platform / org procedure returns.
 *
 * Ported (trimmed) from the TurboTwin webclient's
 * `webclient/src/utils/awaitProcedure.ts`. The org module's `admin_list_projects`
 * (and friends) have `returnType = t.string()` and a body that is a JSON-encoded
 * `{ success, data, error }` envelope; `awaitProcedureJson` resolves with `data`
 * on success and rejects with `Error(error)` otherwise.
 */

// Loose connection type. The runtime contract we depend on is:
//   - `.procedures` exposes accessor functions that return Promises
//   - (optional) `.isActive` boolean for the active-check
// eslint-disable-next-line @typescript-eslint/no-explicit-any
type ProcedureConnection = any;

const DEFAULT_TIMEOUT_MS = 30_000;

export interface AwaitProcedureOptions {
  /** Reject with a timeout error after this many ms. Default 30s. */
  timeoutMs?: number;
}

export interface ProcedureEnvelope<T> {
  success: boolean;
  data?: T;
  error?: string;
}

/**
 * Invoke a procedure call thunk against `conn`, wrapping it in a timeout and a
 * clearer error path. Returns whatever the procedure returns.
 */
export async function awaitProcedure<TReturn>(
  conn: ProcedureConnection,
  call: (c: ProcedureConnection) => Promise<TReturn>,
  options: AwaitProcedureOptions = {},
): Promise<TReturn> {
  if (!conn) {
    throw new Error('awaitProcedure: connection is null');
  }
  if (conn.isActive === false) {
    throw new Error('awaitProcedure: connection is not active');
  }

  const timeoutMs = options.timeoutMs ?? DEFAULT_TIMEOUT_MS;

  let timer: ReturnType<typeof setTimeout> | null = null;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => {
      reject(new Error(`Procedure timed out after ${timeoutMs}ms`));
    }, timeoutMs);
  });

  try {
    return await Promise.race([call(conn), timeout]);
  } finally {
    if (timer) clearTimeout(timer);
  }
}

/**
 * Variant for procedures whose `returnType` is `t.string()` and whose body is a
 * JSON-encoded `{ success, data, error }` envelope. Resolves with `data` on
 * `success === true`, rejects with `Error(error)` otherwise.
 *
 * Some server procedures double-encode the envelope (a JSON string returned
 * inside another JSON string); this helper transparently unwraps both.
 */
export async function awaitProcedureJson<TData>(
  conn: ProcedureConnection,
  call: (c: ProcedureConnection) => Promise<string>,
  options: AwaitProcedureOptions = {},
): Promise<TData> {
  const raw = await awaitProcedure(conn, call, options);
  if (typeof raw !== 'string') {
    throw new Error(`awaitProcedureJson: expected string return, got ${typeof raw}`);
  }

  let parsed: unknown;
  try {
    parsed = JSON.parse(raw);
  } catch (err) {
    throw new Error(
      `awaitProcedureJson: response was not valid JSON: ${(err as Error).message}`,
    );
  }
  // Some server procedures double-encode (JSON string inside JSON).
  if (typeof parsed === 'string') {
    try {
      parsed = JSON.parse(parsed);
    } catch (err) {
      throw new Error(
        `awaitProcedureJson: double-encoded payload was not valid JSON: ${(err as Error).message}`,
      );
    }
  }

  const envelope = parsed as ProcedureEnvelope<TData>;
  if (!envelope || typeof envelope !== 'object') {
    throw new Error('awaitProcedureJson: response did not match envelope shape');
  }
  if (!envelope.success) {
    throw new Error(envelope.error ?? 'Procedure failed');
  }
  return envelope.data as TData;
}
